import React, { useEffect, useState, useCallback } from "react"
import axios from 'axios'
import { Col, Row, Breadcrumb, Collapse, Space, Tabs, Table, Spin, Select, Input } from "antd"
import { CaretRightOutlined } from '@ant-design/icons'
import { startCase } from "lodash"

import { COD_SERVICE_PROVIDERS } from "../../constants/serviceProviders"
import DragUpload from "../Form/DragUpload"
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext"; 

const UNITS = ["RM", "PCT"]

const columnTypes = [
  { 
    title: "Type", 
    dataIndex: "type", 
    render: text => {
      let header = startCase(text).toUpperCase()
      UNITS.forEach(unit => {
        if (header.includes(unit)) {
          header = header.replace(unit, `(${unit})`)
        }
      })

      return header
    },
  },
  ...(COD_SERVICE_PROVIDERS.map(serviceProvider => ({ 
    title: serviceProvider, 
    dataIndex: serviceProvider, 
    render: text => text ? Number(text).toFixed(2) : '-'
  })))
];

const CourierPackagePricing = () => {
  const { permissions } = usePermissions();
  const routes = [
    { path: "", breadcrumbName: "App" },
    { path: "/courier-package/pricing", breadcrumbName: "Courier Package Pricing" },
  ];

  const [isLoading, setIsLoading] = useState(false)
  const [availablePackages, setAvailablePackages] = useState([])
  const [packagePricings, setPackagePricings] = useState({})
  const [activeTab, setActiveTab] = useState(null)
  const [typeSearch, setTypeSearch] = useState("")

  const filterRows = (rows) => {
    if (!rows) return rows

    const search = typeSearch.trim().toLowerCase()
    if (!search) return rows

    return rows.filter((row) => String(row.type || "").toLowerCase().includes(search))
  }

  const fetchPackagePricings = useCallback(async () => {
    if (!activeTab || packagePricings[activeTab]) { 
      return 
    }

    setIsLoading(true)
    const res = await axios.get(`/api/courier-packages/pricings`, { params: { package: activeTab } })

    setPackagePricings(prevPackagePricings => ({
      ...prevPackagePricings,
      [activeTab]: res.data.data
    }))

    setIsLoading(false)
  }, [activeTab])

  const handleUploadSuccess = () => {
    setPackagePricings({})
    setActiveTab("normal")
    fetchPackagePricings()
  }

  useEffect(() => {
    const getAvailablePackages = async () => {
      const response = await axios.get(`/api/courier-packages`)
      const packages = response.data.meta?.packages || []

      setAvailablePackages(packages)
      setActiveTab("normal")
    }

    getAvailablePackages()
  }, [])
  
  useEffect(() => { fetchPackagePricings() }, [activeTab, fetchPackagePricings])

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Courier Package Pricing</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "16px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      {permissions?.includes(PERMISSIONS.UPLOAD_PRICING) && (
        <div className="upload">
          <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
            <Collapse.Panel header="Upload New Pricing" key="upload">
              <DragUpload 
                action={`${process.env.REACT_APP_SERVER_URL}/api/courier-packages/pricing`} 
                accept=".xls, .xlsx"
                method="put"
                onUploadSuccess={handleUploadSuccess}
              />
            </Collapse.Panel>
          </Collapse>
        </div>)}

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Filters" key="filters">
            <Row gutter={12} align="middle">
              <Col>
                <Select
                  showSearch
                  placeholder="Jump to package"
                  style={{ width: 200 }}
                  value={activeTab}
                  onChange={setActiveTab}
                  optionFilterProp="children"
                >
                  {availablePackages.map((type) => (
                    <Select.Option key={type} value={type}>{startCase(type)}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Input.Search
                  placeholder="Filter by type"
                  allowClear
                  onSearch={setTypeSearch}
                  style={{ width: 220 }}
                />
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <div className="table">
          <Tabs activeKey={activeTab} animated={true} onTabClick={tab => setActiveTab(tab)}>
            { availablePackages.length && availablePackages.map(type => (
              <Tabs.TabPane tab={startCase(type)} key={type}>
                <Table
                  rowKey="Id"
                  className="table-section"
                  columns={columnTypes}
                  dataSource={filterRows(packagePricings[type])}
                  loading={isLoading}
                  pagination={{ pageSize: 50 }}
                />
              </Tabs.TabPane>
            )) }
          </Tabs>

          { !availablePackages.length && <Spin size="large" /> }
        </div>
      </Space>
    </div>
  )
}

export default CourierPackagePricing;